import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Users, Smartphone, Send, Radar, ArrowRight, TrendingUp, MapPin, MessageCircle, Map as MapIcon,
} from "lucide-react";
import { ResponsiveContainer, AreaChart, Area, XAxis, Tooltip } from "recharts";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, Spinner, Badge } from "@/components/kit";
import DashboardMap from "@/components/DashboardMap";
import UpgradePromoAlert from "@/components/UpgradePromoAlert";

function Stat({ icon: Icon, label, value, to }) {
  return (
    <Link to={to}>
      <Card className="p-5 hover:-translate-y-0.5 transition-transform cursor-pointer">
        <div className="flex items-center justify-between">
          <div className="w-10 h-10 rounded-xl bg-sky-50 grid place-items-center"><Icon className="w-5 h-5 text-sky-600" /></div>
          <ArrowRight className="w-4 h-4 text-slate-300" />
        </div>
        <p className="font-display text-3xl font-extrabold mt-4">{value}</p>
        <p className="text-sm text-slate-500 mt-1">{label}</p>
      </Card>
    </Link>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [mapData, setMapData] = useState(null);

  useEffect(() => {
    api.get("/analytics/dashboard").then((r) => setData(r.data)).catch(() => {});
    api.get("/analytics/search-map").then((r) => setMapData(r.data)).catch(() => {});
  }, []);

  return (
    <div className="space-y-8">
      {user?.plan === "free" && <UpgradePromoAlert />}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-extrabold">Halo, {user?.name?.split(" ")[0]} 👋</h1>
          <p className="text-slate-500 mt-1">Ringkasan aktivitas akun kamu hari ini.</p>
        </div>
        <Link to="/app/scraper">
          <button className="inline-flex items-center gap-2 rounded-xl bg-sky-500 text-white px-5 py-2.5 text-sm font-semibold hover:bg-sky-600 transition sky-glow" data-testid="dashboard-scrape-cta">
            <Radar className="w-4 h-4" /> Mulai Scraping
          </button>
        </Link>
      </div>

      {!data ? (
        <div className="flex justify-center py-20"><Spinner className="w-7 h-7" /></div>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <Stat icon={Users} label="Total Leads" value={data.total_leads} to="/app/leads" />
            <Stat icon={Smartphone} label="WA Device Aktif" value={`${data.connected_devices}/${data.devices}`} to="/app/devices" />
            <Stat icon={Send} label="Pesan Terkirim" value={data.blast.sent} to="/app/blast" />
            <Stat icon={TrendingUp} label="Closing" value={data.funnel.closed} to="/app/leads" />
          </div>

          <div className="grid lg:grid-cols-3 gap-6">
            <Card className="p-6 lg:col-span-2">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-display font-bold text-lg">Leads Dikumpulkan (14 hari)</h3>
                <Badge color="sky">+{data.total_leads} total</Badge>
              </div>
              <ResponsiveContainer width="100%" height={220}>
                <AreaChart data={data.leads_series}>
                  <defs>
                    <linearGradient id="g" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#0ea5e9" stopOpacity={0.3} />
                      <stop offset="100%" stopColor="#0ea5e9" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                  <Tooltip />
                  <Area type="monotone" dataKey="count" stroke="#0ea5e9" strokeWidth={2.5} fill="url(#g)" />
                </AreaChart>
              </ResponsiveContainer>
            </Card>

            <Card className="p-6">
              <h3 className="font-display font-bold text-lg mb-4">Pipeline</h3>
              <div className="space-y-3">
                {Object.entries(data.funnel).map(([k, v]) => (
                  <div key={k} className="flex items-center justify-between">
                    <span className="text-sm text-slate-600 capitalize">{k}</span>
                    <div className="flex items-center gap-2 flex-1 ml-3">
                      <div className="h-2 flex-1 rounded-full bg-slate-100 overflow-hidden">
                        <div className="h-full bg-sky-500 rounded-full" style={{ width: `${Math.min((v / Math.max(data.total_leads, 1)) * 100, 100)}%` }} />
                      </div>
                      <span className="text-sm font-semibold w-6 text-right">{v}</span>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          </div>

          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display font-bold text-lg flex items-center gap-2"><MapIcon className="w-5 h-5 text-sky-500" /> Peta Sebaran Lead</h3>
              {mapData && <Badge color="sky">{mapData.total_locations} lokasi · {mapData.total_leads} lead</Badge>}
            </div>
            {!mapData ? <div className="h-[340px] grid place-items-center"><Spinner className="w-6 h-6" /></div> :
              mapData.points.length === 0 ? (
                <div className="h-[340px] grid place-items-center text-center text-slate-400 text-sm">
                  <div><MapPin className="w-8 h-8 mx-auto mb-2 text-slate-300" />Belum ada data lokasi. Mulai scraping untuk melihat sebaran lead di peta.</div>
                </div>
              ) : (
                <div className="grid lg:grid-cols-3 gap-4">
                  <div className="lg:col-span-2 rounded-xl overflow-hidden bg-slate-50/50 border border-slate-100">
                    <DashboardMap points={mapData.points} />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs font-semibold text-slate-500 mb-2 uppercase tracking-wide">Provinsi teratas</p>
                    <div className="space-y-1.5 max-h-[300px] overflow-y-auto pr-1">
                      {mapData.provinces.map((p) => (
                        <div key={p.name} className="flex items-center justify-between text-sm" data-testid="map-province-row">
                          <span className="text-slate-600 truncate">{p.name}</span>
                          <span className="font-semibold text-slate-800 ml-2">{p.count}</span>
                        </div>
                      ))}
                    </div>
                    <p className="text-[11px] text-slate-400 mt-3">Arahkan kursor ke titik untuk detail kota & jumlah lead. Ukuran titik mencerminkan volume lead.</p>
                  </div>
                </div>
              )}
          </Card>

          <div className="grid md:grid-cols-2 gap-6">
            <Card className="p-6">
              <h3 className="font-display font-bold text-lg mb-4 flex items-center gap-2"><MapPin className="w-5 h-5 text-sky-500" /> Top Kota</h3>
              {data.top_cities.length === 0 ? <p className="text-sm text-slate-400">Belum ada data.</p> :
                data.top_cities.map((c) => (
                  <div key={c.name} className="flex justify-between py-1.5 text-sm border-b border-slate-50 last:border-0">
                    <span className="text-slate-600">{c.name}</span><span className="font-semibold">{c.count}</span>
                  </div>
                ))}
            </Card>
            <Card className="p-6">
              <h3 className="font-display font-bold text-lg mb-4 flex items-center gap-2"><MessageCircle className="w-5 h-5 text-sky-500" /> Top Kategori</h3>
              {data.top_categories.length === 0 ? <p className="text-sm text-slate-400">Belum ada data.</p> :
                data.top_categories.map((c) => (
                  <div key={c.name} className="flex justify-between py-1.5 text-sm border-b border-slate-50 last:border-0">
                    <span className="text-slate-600">{c.name}</span><span className="font-semibold">{c.count}</span>
                  </div>
                ))}
            </Card>
          </div>
        </>
      )}
    </div>
  );
}
